import { OffsetPagedResults } from '~interfaces';
import Device from '~interfaces/device';
import { SkcDataResponse, SkcOffsetPagedResponse } from '~interfaces/responses';
import SkopeiConnectService from '~services/skopeiConnectService';

import DeviceGroup from '../interfaces/deviceGroup';

class DeviceGroupService extends SkopeiConnectService {
	protected readonly path = 'device-groups';

	/**
	 * Gets a list of device groups.
	 *
	 * @param page The page.
	 * @param pageSize The page size.
	 * @param resourceGroupId Filter on resource group id.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns List of device groups.
	 */
	async getDeviceGroups(
		params: {
			page: number;
			pageSize: number;
			resourceGroupId?: number;
		},
		signal?: AbortSignal
	): Promise<OffsetPagedResults<DeviceGroup>> {
		const endpoint = this.createEndpoint('v2');
		const { data } = await this.client.get<SkcOffsetPagedResponse<DeviceGroupResponse>>(endpoint, {
			signal: signal,
			params: {
				page: params.page,
				'page-size': params.pageSize,
				'resource-group': params.resourceGroupId,
			},
		});

		return this.mapSkcOffsetPagedResponse(data, DeviceGroupService.fromResponse);
	}

	/**
	 * Get a list of device groups for a specific device id.
	 *
	 * @param deviceId The device id to get the device groups for.
	 * @param overwrittenClientId The client id if getting it for another organisation as super admin.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns List of device groups that belongs to the given deviceId
	 */
	async getDeviceGroupsByDeviceId(
		params: {
			deviceId: string;
			overwrittenClientId?: string;
		},
		signal?: AbortSignal
	): Promise<DeviceGroup[]> {
		const endpoint = this.createEndpoint('v2', ['device', params.deviceId]);
		const { data } = await this.client.get<DeviceGroupResponse[]>(endpoint, {
			signal: signal,
			headers:
				params.overwrittenClientId ?
					{ OverwrittenClientId: params.overwrittenClientId }
				:	undefined,
		});

		return data.map(DeviceGroupService.fromResponse);
	}

	public getDeviceGroupsByDeviceIdCacheKey(
		deviceId: string,
		overwrittenClientId?: string
	): (string | undefined)[] {
		return [this.path, 'device', deviceId, overwrittenClientId];
	}

	/**
	 * Gets a device group by the given id
	 *
	 * @param deviceGroupId The id of the device group.
	 * @param overwrittenClientId The client id if getting it for another organisation as super admin.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns A device group if it's found.
	 */
	async getDeviceGroupById(
		params: {
			deviceGroupId: string;
			overwrittenClientId?: string;
		},
		signal?: AbortSignal
	): Promise<DeviceGroup> {
		const endpoint = this.createEndpoint('v1', [params.deviceGroupId]);
		const { data } = await this.client.get<SkcDataResponse<DeviceGroupResponse>>(endpoint, {
			signal: signal,
			headers:
				params.overwrittenClientId ?
					{ OverwrittenClientId: params.overwrittenClientId }
				:	undefined,
		});
		return DeviceGroupService.fromResponse(data.data);
	}

	public getDeviceGroupByIdCacheKey(
		deviceGroupId?: string,
		overwrittenClientId?: string
	): (string | undefined)[] {
		return [this.path, deviceGroupId, overwrittenClientId];
	}

	/**
	 * Model parsing.
	 */
	static fromResponse(response: DeviceGroupResponse): DeviceGroup {
		const { id, resourceGroupId, devices } = response;
		return {
			...response,
			id: id.toString(),
			resourceGroupId: resourceGroupId?.toString() ?? undefined,
			devices: devices?.map(({ id, ...rest }) => {
				return {
					...rest,
					id: id.toString(),
				} as Device;
			}),
		};
	}
}

interface DeviceGroupResponse {
	id: number;
	resourceGroupId: number;
	name: string;
	description?: string;
	devices?: Device[];
}

export default DeviceGroupService;
